'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { TFunction, useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import { PRODUCT_FEED_TYPE } from '@src/quince-site-common/src/services/productFeedService/productFeedService.types'
import ProductListTwo from '@src/SectionRenderer/sections/productListTwo'
import { ProductListTwoSectionConfig } from '@src/SectionRenderer/sections/productListTwo/ProductListTwo'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import SearchResultsSection from './searchResultsSection/SearchResultsSection'

// CLS mitigation: while the recommendation (best-sellers) section is loading, reserve
// full-viewport height and hide it so the footer doesn't jump up and back down once it
// loads. Panda-scoped equivalent of the legacy `.searchRecommendationLoadingClass`
// SCSS rule (search.module.scss) — hoisted to module scope (never wrap css() in useMemo).
const searchRecommendationLoadingClass = css({ minHeight: '100vh', opacity: 0 })

function getProductListTwoProps(t: TFunction): ProductListTwoSectionConfig {
  return {
    // @i18n-ignore
    typename: 'CustomSection',
    // @i18n-ignore
    id: 'searchPageaBestSellers',
    // Figma DS 2.0 `WidgetRecommendation` root fill is `neutral/paperwhite` (#F7F7F5),
    // not offwhite (#EEEEEC). `lightestGray` is the Contentful color key that maps to the
    // DS `paperwhite` token (CONTENTFUL_COLOR_TO_DS_VALUE / section-background.ts), so the
    // search Best Sellers band paints paperwhite to match nodes 5718:34230 / 5718:34240.
    // eslint-disable-next-line i18next/no-literal-string
    classNames: ['background-color--lightestGray'],
    influencers: [],
    layoutType: LAYOUT_TYPES.DEFAULT,
    baseDynamicClasses: [],
    mobileDynamicClassesOverrides: [],
    classNamesMobileOverrides: [],
    productFeedType: PRODUCT_FEED_TYPE.CUSTOMER_TOP_PRODUCTS,
    productFeedLimit: 4,
    productFeedMetaData: undefined,
    heading: t('best_sellers'),
    // Search Best Seller is the one product-list-two consumer that keeps the decorative
    // ivy-headline title; every other (reco-swimlane) instance uses the default grosa.
    headingFont: 'ivy',
    // headingClassname intentionally omitted: ProductListTwoDS owns heading styling via
    // Panda tokens and ignores it (see product-list-two-ds.tsx props-not-forwarded note).
  }
}

export default function SearchPageDS() {
  const { t } = useTranslation('common')
  // Load the search results section first, then load the product list two - CLS mitigation
  const [searchLoaded, setSearchLoaded] = useState(false)
  useEffect(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.SEARCH_PAGE_VIEW, {})
  }, [])

  return (
    <>
      <SearchResultsSection setSearchLoaded={setSearchLoaded} />
      {/* loadingContainerClassName intentionally not passed: ProductListTwoDS has no
          skeleton loader and ignores it (product-list-two-ds.tsx). */}
      <div className={searchLoaded ? undefined : searchRecommendationLoadingClass}>
        <ProductListTwo sectionConfig={getProductListTwoProps(t)} />
      </div>
    </>
  )
}
